import { useState, useEffect, useCallback } from 'react';
import { replayQueue, getQueueLength } from '../offlineQueue';
import { toast } from 'sonner';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [pendingCount, setPendingCount] = useState(() => getQueueLength());

  const syncPendingCount = useCallback(() => {
    setPendingCount(getQueueLength());
  }, []);

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
      toast.success('Back online \u2014 syncing your data\u2026');
      replayQueue().then(({ succeeded, failed }) => {
        if (succeeded > 0 && failed === 0) {
          toast.success('All caught up!');
        } else if (succeeded > 0) {
          toast(`Synced ${succeeded} item${succeeded > 1 ? 's' : ''} \u2014 ${failed} still waiting`);
        }
        syncPendingCount();
      });
    }

    function handleOffline() {
      setIsOnline(false);
    }

    function handleQueueChange() {
      syncPendingCount();
    }

    function handleSWMessage(event: MessageEvent) {
      if (event.data?.type === 'replay-offline-queue') {
        replayQueue().then(() => syncPendingCount());
      }
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('cw-queue-change', handleQueueChange);
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('cw-queue-change', handleQueueChange);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, [syncPendingCount]);

  // Register background sync as progressive enhancement when items are queued
  useEffect(() => {
    if (pendingCount > 0 && 'serviceWorker' in navigator && 'SyncManager' in window) {
      navigator.serviceWorker.ready.then(reg => {
        (reg as any).sync?.register('cw-offline-sync').catch(() => {});
      });
    }
  }, [pendingCount]);

  return { isOnline, pendingCount };
}
